import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Loader2, MessageSquare, CheckCircle2, AlertTriangle, Mic, Square, Bookmark } from 'lucide-react';
import { youtubeManagerChat, transcribeAndRefineAudio, ManagerChatChange } from '../services/geminiService';

// Everything sent to the assistant per video; only the first fields are also used to render proposals.
export interface ChatVideoSnapshot {
    id: string;
    title: string | null;
    note: string;
    descriptionStart: string;
    onGithub: boolean;
    [extra: string]: unknown;
}

type ChangeState = 'pending' | 'applying' | 'done' | string; // any other string = error message

interface ChatMsg {
    role: 'user' | 'ai';
    text: string;
    changes?: ManagerChatChange[];
    selected?: boolean[];
    states?: ChangeState[];
}

interface Props {
    language: string;
    getSnapshot: () => ChatVideoSnapshot[];
    onApplyChange: (change: ManagerChatChange) => Promise<void>;
    onOpenVideo: (videoId: string) => void;
    onClose: () => void;
}

const SAVED_PROMPTS_KEY = 'sb_yt_chat_saved_prompts';
interface SavedPrompt { id: string; text: string; }

const FIELD_LABEL: Record<string, { vn: string; en: string }> = {
    title: { vn: 'Tiêu đề', en: 'Title' },
    description: { vn: 'Mô tả', en: 'Description' },
    showOnGithub: { vn: 'Hiện trên GitHub', en: 'Show on GitHub' },
};

export const YouTubeManagerChat: React.FC<Props> = ({ language, getSnapshot, onApplyChange, onOpenVideo, onClose }) => {
    const vn = language === 'vietnamese';
    const [messages, setMessages] = useState<ChatMsg[]>([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const endRef = useRef<HTMLDivElement>(null);

    // Saved questions — kept on this device so a recurring check is one click, not retyping.
    const [savedPrompts, setSavedPrompts] = useState<SavedPrompt[]>(() => {
        try { return JSON.parse(localStorage.getItem(SAVED_PROMPTS_KEY) || '[]'); } catch { return []; }
    });
    const persistPrompts = (list: SavedPrompt[]) => {
        setSavedPrompts(list);
        try { localStorage.setItem(SAVED_PROMPTS_KEY, JSON.stringify(list)); } catch { /* ignore */ }
    };
    const savePrompt = (text: string) => {
        const t = text.trim();
        if (!t || savedPrompts.some(p => p.text === t)) return;
        persistPrompts([...savedPrompts, { id: `${Date.now()}`, text: t }].slice(-20));
    };

    // Voice input — record, transcribe with the same AI transcription the note editor uses, then send.
    const [recording, setRecording] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const [voiceError, setVoiceError] = useState('');
    const recorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const toggleRecording = async () => {
        setVoiceError('');
        if (recording) { recorderRef.current?.stop(); return; }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mimeType = ['audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'].find(t => MediaRecorder.isTypeSupported(t)) || '';
            const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
            recorderRef.current = mr;
            chunksRef.current = [];
            mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            mr.onstop = async () => {
                stream.getTracks().forEach(t => t.stop());
                setRecording(false);
                const blob = new Blob(chunksRef.current, { type: mr.mimeType || mimeType || 'audio/webm' });
                if (blob.size < 1000) return;
                setTranscribing(true);
                try {
                    const dataUrl: string = await new Promise((resolve, reject) => {
                        const r = new FileReader();
                        r.onloadend = () => resolve(r.result as string);
                        r.onerror = reject;
                        r.readAsDataURL(blob);
                    });
                    const text = await transcribeAndRefineAudio(dataUrl.split(',')[1], language as any);
                    if (text?.trim()) await send(text);
                } catch (e: any) {
                    setVoiceError(`${vn ? 'Không nhận diện được giọng nói' : 'Could not transcribe audio'}: ${e?.message || e}`);
                } finally { setTranscribing(false); }
            };
            mr.start();
            setRecording(true);
        } catch (e: any) {
            setVoiceError(vn ? 'Không truy cập được micro — kiểm tra quyền micro của trình duyệt.' : 'Microphone unavailable — check the browser permission.');
        }
    };
    useEffect(() => () => { if (recorderRef.current?.state === 'recording') recorderRef.current.stop(); }, []);

    // Assistant text -> plain text with clickable video chips ([[id]]) and **bold**.
    const renderRich = (text: string): React.ReactNode[] =>
        text.split(/(\[\[[^\]]+\]\]|\*\*[^*]+\*\*)/g).filter(Boolean).map((part, i) => {
            const chip = part.match(/^\[\[([^\]]+)\]\]$/);
            if (chip) {
                const id = chip[1].trim();
                const snap = getSnapshot().find(v => v.id === id);
                if (!snap) return <span key={i}>{id}</span>;
                return (
                    <button key={i} onClick={() => onOpenVideo(id)} title={vn ? 'Mở video này' : 'Open this video'}
                        className="inline-flex items-center max-w-[210px] align-middle mx-0.5 px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-800/60 text-[11px] font-semibold transition-colors">
                        <span className="truncate">{snap.title || snap.note || id}</span>
                    </button>
                );
            }
            const bold = part.match(/^\*\*([^*]+)\*\*$/);
            return bold ? <strong key={i}>{bold[1]}</strong> : <span key={i}>{part}</span>;
        });

    useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

    const suggestions = vn
        ? ['Video nào chưa AI Check hoặc check đã cũ?', 'Liệt kê các video và tiêu đề hiện tại', 'Video nào có mô tả trống hoặc quá ngắn?']
        : ['Which videos have no AI Check or an outdated one?', 'List the videos and their current titles', 'Which videos have an empty or very short description?'];

    const send = async (text: string) => {
        const message = text.trim();
        if (!message || loading) return;
        const history = messages.map(m => ({ role: m.role, text: m.text }));
        setMessages(prev => [...prev, { role: 'user', text: message }]);
        setInput('');
        setLoading(true);
        try {
            const res = await youtubeManagerChat(getSnapshot(), history, message, language);
            const changes = res.changes || [];
            setMessages(prev => [...prev, {
                role: 'ai', text: res.reply || (vn ? '(không có phản hồi)' : '(no reply)'),
                changes: changes.length ? changes : undefined,
                selected: changes.map(() => true),
                states: changes.map(() => 'pending'),
            }]);
        } catch (e: any) {
            setMessages(prev => [...prev, { role: 'ai', text: `${vn ? 'Lỗi' : 'Error'}: ${e?.message || e}` }]);
        } finally { setLoading(false); }
    };

    const toggleSelected = (mi: number, ci: number) =>
        setMessages(prev => prev.map((m, i) => i === mi && m.selected ? { ...m, selected: m.selected.map((v, j) => j === ci ? !v : v) } : m));

    const applySelected = async (mi: number) => {
        const msg = messages[mi];
        if (!msg?.changes || !msg.selected) return;
        for (let ci = 0; ci < msg.changes.length; ci++) {
            if (!msg.selected[ci] || msg.states?.[ci] === 'done') continue;
            const setState = (st: ChangeState) => setMessages(prev => prev.map((m, i) => i === mi && m.states ? { ...m, states: m.states.map((s, j) => j === ci ? st : s) } : m));
            setState('applying');
            try {
                await onApplyChange(msg.changes[ci]);
                setState('done');
            } catch (e: any) {
                setState(e?.message === 'not_connected' ? (vn ? 'YouTube chưa kết nối / hết phiên' : 'YouTube not connected / session expired') : (e?.message || 'Failed'));
            }
        }
    };

    const snapById = (id: string) => getSnapshot().find(v => v.id === id);

    const currentValue = (c: ManagerChatChange): string => {
        const s = snapById(c.videoId);
        if (!s) return '';
        if (c.field === 'title') return s.title || '';
        if (c.field === 'description') return s.descriptionStart;
        return s.onGithub ? (vn ? 'đang hiện' : 'shown') : (vn ? 'đang ẩn' : 'hidden');
    };

    return (
        <div className="fixed top-0 right-0 bottom-0 w-[380px] max-w-[92vw] z-[201] bg-white dark:bg-slate-900 border-l border-gray-200 dark:border-slate-700 shadow-xl flex flex-col">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-slate-800 flex-shrink-0">
                <MessageSquare size={15} className="text-indigo-500" />
                <h3 className="font-bold text-sm text-gray-900 dark:text-white flex-1">{vn ? 'Trợ lý YouTube Manager' : 'YouTube Manager assistant'}</h3>
                <button onClick={onClose} className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg"><X size={14} className="text-gray-400" /></button>
            </div>

            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
                {messages.length === 0 && (
                    <div className="space-y-2">
                        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                            {vn
                                ? 'Hỏi về các video của notebook đang mở (tiêu đề, mô tả, AI Check, hiện trên GitHub…) hoặc nhờ đề xuất sửa. Mọi thay đổi chỉ được áp dụng sau khi bạn xác nhận. Xem được playlist sau khi bạn bấm “Đồng bộ playlist”; chưa xem được khai báo trẻ em.'
                                : "Ask about the videos in the open notebook (titles, descriptions, AI Check, GitHub visibility…) or ask for edits. Nothing is applied until you confirm. Playlists are visible after you press “Sync playlists”; the kids declaration isn't visible yet."}
                        </p>
                        {suggestions.filter(sug => !savedPrompts.some(p => p.text === sug)).map(sug => (
                            <button key={sug} onClick={() => send(sug)} className="block w-full text-left text-xs px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300 hover:border-indigo-300 hover:text-indigo-600 transition-colors">{sug}</button>
                        ))}
                    </div>
                )}

                {messages.map((m, mi) => (
                    <div key={mi} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                        <div className={`max-w-[92%] rounded-xl px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap break-words ${m.role === 'user' ? 'bg-indigo-500 text-white' : 'bg-gray-100 dark:bg-slate-800 text-gray-800 dark:text-gray-200'}`}>
                            {m.role === 'ai' ? renderRich(m.text) : m.text}
                            {m.role === 'user' && (
                                <button onClick={() => savePrompt(m.text)} title={vn ? 'Lưu câu hỏi này để dùng lại' : 'Save this question for reuse'}
                                    className="ml-1.5 align-middle opacity-60 hover:opacity-100">
                                    <Bookmark size={11} className={savedPrompts.some(p => p.text === m.text.trim()) ? 'fill-current' : ''} />
                                </button>
                            )}
                            {m.changes && m.selected && m.states && (
                                <div className="mt-2 space-y-2 whitespace-normal">
                                    {m.changes.map((c, ci) => {
                                        const st = m.states![ci];
                                        const snap = snapById(c.videoId);
                                        const label = FIELD_LABEL[c.field] ?? { vn: c.field, en: c.field };
                                        return (
                                            <div key={ci} className="rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 p-2 text-gray-700 dark:text-gray-300">
                                                <label className="flex items-start gap-2 cursor-pointer">
                                                    <input type="checkbox" className="mt-0.5 accent-indigo-500" checked={m.selected![ci]} disabled={st === 'done' || st === 'applying'} onChange={() => toggleSelected(mi, ci)} />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-semibold text-[11px] truncate">{vn ? label.vn : label.en} · {snap?.title || snap?.note || c.videoId}</p>
                                                        {st !== 'done' && currentValue(c) && <p className="text-[11px] text-gray-400 line-through break-words">{currentValue(c).slice(0, 160)}</p>}
                                                        {c.field === 'description'
                                                            ? <details><summary className="text-[11px] text-green-700 dark:text-green-400 cursor-pointer">{vn ? 'Xem mô tả mới' : 'Show new description'}</summary><p className="text-[11px] text-green-700 dark:text-green-400 whitespace-pre-wrap break-words mt-1">{c.newValue}</p></details>
                                                            : <p className="text-[11px] text-green-700 dark:text-green-400 break-words">→ {c.field === 'showOnGithub' ? (c.newValue === 'true' ? (vn ? 'hiện' : 'shown') : (vn ? 'ẩn' : 'hidden')) : c.newValue}</p>}
                                                        {c.reason && <p className="text-[10px] text-gray-400 mt-0.5">{c.reason}</p>}
                                                        {st === 'done' && <p className="text-[11px] text-green-600 flex items-center gap-1"><CheckCircle2 size={11} />{vn ? 'Đã áp dụng' : 'Applied'}</p>}
                                                        {st === 'applying' && <p className="text-[11px] text-gray-400 flex items-center gap-1"><Loader2 size={10} className="animate-spin" />{vn ? 'Đang áp dụng…' : 'Applying…'}</p>}
                                                        {st !== 'pending' && st !== 'applying' && st !== 'done' && <p className="text-[11px] text-red-500 flex items-start gap-1 break-words"><AlertTriangle size={11} className="mt-0.5 flex-shrink-0" />{st}</p>}
                                                    </div>
                                                </label>
                                            </div>
                                        );
                                    })}
                                    <button onClick={() => applySelected(mi)}
                                        disabled={!m.selected.some((sel, ci) => sel && m.states![ci] !== 'done' && m.states![ci] !== 'applying') || m.states.some(s => s === 'applying')}
                                        className="w-full py-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-600 disabled:opacity-40 text-white font-bold text-[11px] transition-colors">
                                        {vn ? 'Áp dụng các mục đã chọn' : 'Apply selected'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {loading && <div className="flex justify-start"><div className="rounded-xl px-3 py-2 bg-gray-100 dark:bg-slate-800"><Loader2 size={13} className="animate-spin text-gray-400" /></div></div>}
                <div ref={endRef} />
            </div>

            {savedPrompts.length > 0 && (
                <div className="px-3 pt-2 border-t border-gray-100 dark:border-slate-800 flex flex-wrap gap-1.5 max-h-24 overflow-y-auto flex-shrink-0">
                    {savedPrompts.map(p => (
                        <span key={p.id} className="group inline-flex items-center max-w-full rounded-full border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 text-[11px]">
                            <button onClick={() => send(p.text)} disabled={loading} title={p.text} className="px-2.5 py-1 truncate max-w-[280px] disabled:opacity-50">{p.text}</button>
                            <button onClick={() => persistPrompts(savedPrompts.filter(x => x.id !== p.id))} title={vn ? 'Xóa câu hỏi đã lưu' : 'Remove saved question'} className="pr-2 opacity-40 hover:opacity-100"><X size={10} /></button>
                        </span>
                    ))}
                </div>
            )}
            {voiceError && <p className="px-3 pt-1 text-[11px] text-red-500 flex-shrink-0">{voiceError}</p>}
            <div className="p-3 border-t border-gray-100 dark:border-slate-800 flex gap-2 flex-shrink-0">
                <textarea
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); } }}
                    rows={2}
                    placeholder={recording ? (vn ? 'Đang nghe… bấm ■ để gửi' : 'Listening… press ■ to send') : transcribing ? (vn ? 'Đang chuyển giọng nói thành chữ…' : 'Transcribing…') : (vn ? 'Hỏi hoặc nhờ sửa… (Enter để gửi)' : 'Ask or request edits… (Enter to send)')}
                    className="flex-1 text-xs border border-gray-200 dark:border-slate-600 rounded-lg px-2.5 py-2 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none"
                />
                <div className="self-end flex flex-col gap-1.5">
                    <button onClick={() => savePrompt(input)} disabled={!input.trim()} title={vn ? 'Lưu câu hỏi này để dùng lại' : 'Save this question for reuse'}
                        className="p-2 rounded-lg border border-gray-200 dark:border-slate-600 text-gray-400 hover:text-indigo-600 hover:border-indigo-300 disabled:opacity-30 transition-colors">
                        <Bookmark size={13} />
                    </button>
                    <button onClick={toggleRecording} disabled={transcribing || loading} title={recording ? (vn ? 'Dừng và gửi' : 'Stop and send') : (vn ? 'Nói thay vì gõ' : 'Speak instead of typing')}
                        className={`p-2 rounded-lg border transition-colors disabled:opacity-40 ${recording ? 'bg-red-500 border-red-500 text-white animate-pulse' : 'border-gray-200 dark:border-slate-600 text-gray-500 hover:text-indigo-600 hover:border-indigo-300'}`}>
                        {transcribing ? <Loader2 size={13} className="animate-spin" /> : recording ? <Square size={13} /> : <Mic size={13} />}
                    </button>
                    <button onClick={() => send(input)} disabled={loading || !input.trim()} className="p-2.5 rounded-lg bg-indigo-500 hover:bg-indigo-600 disabled:opacity-40 text-white transition-colors">
                        <Send size={14} />
                    </button>
                </div>
            </div>
        </div>
    );
};
